import { useQueryClient } from '@tanstack/react-query';
import {
  hasRoleAtLeast,
  type AuthResponseDto,
  type LoginInput,
  type RegisterInput,
  type UserDto,
  type UserRole,
} from '@vmt/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, onSessionChange, refreshSession, setSession } from '../lib/api';

type AuthStatus = 'loading' | 'authenticated' | 'guest';

interface AuthContextValue {
  status: AuthStatus;
  user: UserDto | null;
  login: (input: LoginInput) => Promise<UserDto>;
  register: (input: RegisterInput) => Promise<UserDto>;
  logout: () => Promise<void>;
  /** True if the user has at least the given role. */
  can: (role: UserRole) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserDto | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  // Keep React state in sync with the token store and refresh proactively before expiry.
  useEffect(() => {
    const unsubscribe = onSessionChange((auth: AuthResponseDto | null) => {
      window.clearTimeout(refreshTimer.current);
      if (!auth) {
        setUser(null);
        setStatus('guest');
        return;
      }
      setUser(auth.user);
      setStatus('authenticated');
      const delay = Math.max(30, auth.expiresIn - 60) * 1000;
      refreshTimer.current = window.setTimeout(() => void refreshSession(), delay);
    });
    // Restore the session from the refresh cookie (also covers network errors → guest).
    void refreshSession().then((auth) => {
      if (!auth) setStatus((current) => (current === 'loading' ? 'guest' : current));
    });
    return () => {
      unsubscribe();
      window.clearTimeout(refreshTimer.current);
    };
  }, []);

  const login = useCallback(
    async (input: LoginInput) => {
      const auth = await api<AuthResponseDto>('/auth/login', { method: 'POST', body: input });
      setSession(auth);
      await queryClient.invalidateQueries();
      return auth.user;
    },
    [queryClient],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const auth = await api<AuthResponseDto>('/auth/register', { method: 'POST', body: input });
      setSession(auth);
      await queryClient.invalidateQueries();
      return auth.user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } finally {
      setSession(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    const me = await api<UserDto>('/auth/me');
    setUser(me);
  }, []);

  const can = useCallback((role: UserRole) => hasRoleAtLeast(user?.role, role), [user]);

  const value = useMemo(
    () => ({ status, user, login, register, logout, can, refreshUser }),
    [status, user, login, register, logout, can, refreshUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
